import {
  all,
  call,
  put,
  race,
  select,
  take,
  fork,
  delay,
  debounce,
  takeEvery,
  takeLatest,
  actionChannel,
} from 'redux-saga/effects';
import {
  patchProjectBlocks,
  patchProjectConfig,
  deleteProjectBlocks,
  patchProjectBlockOrder,
  addProjectBlocks,
} from '@src/apis/project';
import {
  createProjectCaptionTemplate,
  updateProjectCaptionTemplate,
} from '@src/apis/captionTemplate';
import {
  computeAutoSaveDiff,
  planAutoSaveRequests,
  serializeBlockForSave,
  pickChangedBlocksContent,
} from '@src/services/autoSave';
import {
  countTextLengthBlocks,
  buildBlockCreatePayload,
  buildVideoSettingsPayload,
  buildCaptionTemplatePayload,
  isProjectCaptionStyleId,
  withCustomCaptionStyle,
} from '@src/services/project';
import {
  MAX_TOTAL_CHARACTERS,
  AUTO_SAVE_STATUS,
  AUTO_SAVE_REQUEST_TYPE,
  AUTO_SAVE_DEBOUNCE_MS,
  AUTO_SAVE_RETRY_MS,
  AUTO_SAVE_SAVED_DURATION_MS,
  AUTO_SAVE_MAX_CONCURRENT_ADD_REQUESTS,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import { ERROR_CODE } from '@src/errors/code';
import actions from '../actions';
import { actionTypes } from './actions';
import { actionTypes as historyActionTypes } from '../history/actions';

const EDIT_ACTIONS = [
  actionTypes.UPDATE_BLOCK,
  actionTypes.UPDATE_MULTIPLE_BLOCKS,
  actionTypes.UPDATE_BLOCK_ELEMENTS,
  actionTypes.UPDATE_TITLE,
  actionTypes.SET_AUDIO_TYPE,
  actionTypes.DELETE_BLOCK,
  actionTypes.DELETE_BLOCKS,
  actionTypes.UPDATE_ORDER_BLOCK,
  actionTypes.ADD_BLOCK,
  actionTypes.UPDATE_VIDEO_SETTINGS,
  actionTypes.APPLY_CAPTION_STYLE,
  actionTypes.UPDATE_CAPTION_STYLE,
  actionTypes.ADD_MUSIC_CLIP,
  actionTypes.DUPLICATE_MUSIC_CLIP,
  actionTypes.UPDATE_MUSIC_CLIP,
  actionTypes.DELETE_MUSIC_CLIP,
  actionTypes.SET_TRACK_VOLUME,
];

// Match genuine user edits only. Programmatic dispatches flagged with
// `meta.skipAutoSave` (e.g. initial project load setting audioType/title) are
// ignored so opening a project doesn't fire a spurious IDLE→SAVED→IDLE pass.
const isUserEditAction = (action) =>
  EDIT_ACTIONS.includes(action.type) && !action.meta?.skipAutoSave;

// Undo/redo (Dubbing) restores project state through the history system's single
// write action, HISTORY_APPLY_PATCHES. Treat it like an edit so the restored
// state is persisted — without touching the history flow itself. When auto-save
// is off, the downstream pass is a no-op (getAutoSaveContext returns null).
const isHistoryRestoreAction = (action) =>
  action.type === historyActionTypes.HISTORY_APPLY_PATCHES;

// Any change that should schedule an auto-save: a user edit OR a history restore.
const isAutoSaveTriggerAction = (action) =>
  isUserEditAction(action) || isHistoryRestoreAction(action);

// Dispatch helper for the auto-save status (shorter than
// put(actions.project.setAutoSaveStatus(...))).
const putAutoSaveStatus = (status) =>
  put(actions.project.setAutoSaveStatus(status));

function* callSaveEndpoint(projectId, request) {
  switch (request.type) {
    case AUTO_SAVE_REQUEST_TYPE.UPDATE_CONFIG:
      return yield call(patchProjectConfig, projectId, request.payload);
    case AUTO_SAVE_REQUEST_TYPE.UPDATE_BLOCK_FIELDS:
      return yield call(patchProjectBlocks, projectId, request.payload);
    case AUTO_SAVE_REQUEST_TYPE.DELETE_BLOCKS:
      return yield call(deleteProjectBlocks, projectId, request.payload);
    case AUTO_SAVE_REQUEST_TYPE.REORDER_BLOCKS:
      return yield call(patchProjectBlockOrder, projectId, request.payload);
    case AUTO_SAVE_REQUEST_TYPE.ADD_BLOCKS:
      return yield call(addProjectBlocks, projectId, request.payload);
    case AUTO_SAVE_REQUEST_TYPE.SAVE_CAPTION_CONFIG: {
      const { styleId, config } = request.payload;
      // A style the project owns is replaced in place; a system style is not the
      // project's to change, so editing one takes a copy.
      if (isProjectCaptionStyleId(styleId)) {
        return yield call(updateProjectCaptionTemplate, styleId, { config });
      }
      return yield call(createProjectCaptionTemplate, { projectId, config });
    }
    default:
      return undefined;
  }
}

function* sendSaveRequest(projectId, request) {
  const response = yield call(callSaveEndpoint, projectId, request);
  // A 2xx HTTP response can still carry a Backend failure envelope
  // ({ status: 0, errorCode, errorMessage }); axios doesn't reject on it, so a
  // bare try/catch would treat it as a successful save. Surface it as an error,
  // forwarding the errorCode so persistAutoSave can tell "project deleted"
  // (1040, no retry) apart from a transient failure (retry).
  if (response && !response.status) {
    const error = new Error(response.errorMessage);
    error.errorCode = response.errorCode;
    throw error;
  }
  return response;
}

function* retryAutoSaveLater() {
  // Wait the retry delay, but bail out immediately if auto-save gets toggled
  // (the project unmount dispatches setAutoSaveEnabled(false)) so a stale retry
  // never fires after the user has left the project.
  const { timedOut } = yield race({
    timedOut: delay(AUTO_SAVE_RETRY_MS),
    toggled: take(actionTypes.SET_AUTO_SAVE_ENABLED),
  });
  if (!timedOut) return;
  const { autoSaveEnabled, projectId } = yield select((s) => s.project);
  if (!autoSaveEnabled || !projectId) return;
  yield put(actions.project.autoSaveTrigger());
}

// Collect everything one auto-save pass needs from the store.
// Returns null when auto-save is off or there is no project yet -> skip.
function* getAutoSaveContext() {
  const project = yield select((s) => s.project);
  const {
    autoSaveEnabled,
    projectId,
    product,
    lastSaved,
    title,
    audioType,
    voiceDefault,
    videoSettings,
    musicClips,
    volumes,
    autoSaveVideoConfig,
    source,
  } = project;
  const { blocks, blockIds } = project.multiBlock || {};
  // Once the project is known-deleted, every save would 1040 again — stop.
  if (
    !autoSaveEnabled ||
    !projectId ||
    project.autoSaveStatus === AUTO_SAVE_STATUS.DELETED
  )
    return null;
  return {
    projectId,
    product,
    blocks,
    blockIds,
    lastSaved,
    title,
    audioType,
    voiceDefault,
    videoSettings,
    musicClips,
    volumes,
    autoSaveVideoConfig,
    source,
  };
}

const isOverCharacterLimit = (blocks) =>
  countTextLengthBlocks(blocks, 'text') > MAX_TOTAL_CHARACTERS;

// Send a group of requests concurrently, but at most
// AUTO_SAVE_MAX_CONCURRENT_ADD_REQUESTS in flight at once (process in chunks).
function* sendConcurrently(projectId, requests) {
  for (
    let i = 0;
    i < requests.length;
    i += AUTO_SAVE_MAX_CONCURRENT_ADD_REQUESTS
  ) {
    const chunk = requests.slice(i, i + AUTO_SAVE_MAX_CONCURRENT_ADD_REQUESTS);
    yield all(
      chunk.map((request) => call(sendSaveRequest, projectId, request)),
    );
  }
}

// Send save requests preserving order. Consecutive ADD_BLOCKS requests target
// distinct anchors, so they are sent concurrently (capped) to keep a scattered
// undo fast; every other request stays sequential (and ADDs finish before a
// later REORDER references the new blocks).
function* sendAllRequests(projectId, requests) {
  let i = 0;
  while (i < requests.length) {
    if (requests[i].type !== AUTO_SAVE_REQUEST_TYPE.ADD_BLOCKS) {
      yield call(sendSaveRequest, projectId, requests[i]);
      i += 1;
    } else {
      const addRequests = [];
      while (
        i < requests.length &&
        requests[i].type === AUTO_SAVE_REQUEST_TYPE.ADD_BLOCKS
      ) {
        addRequests.push(requests[i]);
        i += 1;
      }
      yield call(sendConcurrently, projectId, addRequests);
    }
  }
}

// Turn planned requests into what the endpoints take. The engine keeps
// videoSettings in its raw store form; the Backend shape is built here, where the
// feature flags live.
//
// UPDATE_CONFIG is built from `meta` rather than its own payload: the two are the
// same object until a caption save answers with a new style id, and then only
// `meta` has it.
const toApiRequests = (requests, meta, autoSaveVideoConfig) =>
  requests
    .map((request) => {
      if (request.type !== AUTO_SAVE_REQUEST_TYPE.UPDATE_CONFIG) return request;
      const { videoSettings: rawVideoSettings, ...restConfig } = meta;
      if (!rawVideoSettings) return { ...request, payload: restConfig };
      const builtVideoSettings = buildVideoSettingsPayload({
        aspectRatioEnabled: autoSaveVideoConfig?.aspectRatioEnabled,
        backgroundColorEnabled: autoSaveVideoConfig?.backgroundColorEnabled,
        videoSettings: rawVideoSettings,
      });
      const captionTemplate = autoSaveVideoConfig?.captionEnabled
        ? buildCaptionTemplatePayload(rawVideoSettings?.captionStyle)
        : undefined;
      return {
        ...request,
        payload: {
          ...restConfig,
          ...(builtVideoSettings ? { videoSettings: builtVideoSettings } : {}),
          ...(captionTemplate ? { captionTemplate } : {}),
        },
      };
    })
    .filter((request) => {
      // Already sent on its own, for the id it answers with.
      if (request.type === AUTO_SAVE_REQUEST_TYPE.SAVE_CAPTION_CONFIG)
        return false;
      // Drop an UPDATE_CONFIG that ends up empty, e.g. every field it holds is
      // behind a disabled flag.
      return (
        request.type !== AUTO_SAVE_REQUEST_TYPE.UPDATE_CONFIG ||
        Object.keys(request.payload).length > 0
      );
    });

// Persist the changes: call the API -> update the baseline -> report SAVED.
// On failure -> report ERROR and schedule a retry.
function* persistAutoSave(context, diff) {
  const { projectId, product, blocks, blockIds, voiceDefault, videoSettings } =
    context;
  // Added blocks become part of the baseline so they aren't re-added next pass.
  const savedBaseline = pickChangedBlocksContent(
    [...diff.changedIds, ...diff.addedIds],
    blocks,
    product,
  );
  const isPresentation = product === PROJECT_PRODUCT.PRESENTATION;
  const requests = planAutoSaveRequests(diff, {
    blockIds,
    blocks,
    serializeBlock: (block, fields) =>
      serializeBlockForSave(block, product, fields),
    serializeNewBlock: (block, id) =>
      buildBlockCreatePayload(block, id, {
        voiceDefault,
        isPresentation,
        autoSaveEnabled: true,
      }),
  });

  yield putAutoSaveStatus(AUTO_SAVE_STATUS.SAVING);
  try {
    // The style document goes first: the assignment that follows names the id
    // it answers with.
    let savedMeta = diff.meta;
    const captionRequest = requests.find(
      (request) => request.type === AUTO_SAVE_REQUEST_TYPE.SAVE_CAPTION_CONFIG,
    );
    if (captionRequest) {
      const response = yield call(sendSaveRequest, projectId, captionRequest);
      // id, not _id: the axios layer camelCases the response.
      const savedId = response?.result?.captionTemplate?.id;
      if (savedId && savedId !== captionRequest.payload.styleId) {
        yield put(actions.project.setProjectCaptionTemplateId(savedId));
        // The baseline has to record the id that was saved, or the next pass
        // reads the swap as another change.
        const raw = savedMeta.videoSettings ?? videoSettings;
        savedMeta = {
          ...savedMeta,
          videoSettings: withCustomCaptionStyle({
            ...raw,
            captionStyle: { ...raw?.captionStyle, styleId: savedId },
          }),
        };
      }
    }

    yield call(
      sendAllRequests,
      projectId,
      toApiRequests(requests, savedMeta, context.autoSaveVideoConfig),
    );
    yield put(
      actions.project.patchAutoSaveBaseline({
        blockIds,
        blocks: savedBaseline,
        removedIds: diff.removedIds,
        meta: savedMeta,
      }),
    );
    yield putAutoSaveStatus(AUTO_SAVE_STATUS.SAVED);
  } catch (error) {
    // Project deleted elsewhere — no point retrying; show the deleted banner.
    if (error?.errorCode === ERROR_CODE.PROJECT_NOT_FOUND) {
      yield putAutoSaveStatus(AUTO_SAVE_STATUS.DELETED);
      return;
    }
    yield putAutoSaveStatus(AUTO_SAVE_STATUS.ERROR);
    // The style names a face the catalogue does not have, so a retry sends the
    // same config to the same catalogue. The next edit is the moment to try again.
    if (error?.errorCode === ERROR_CODE.CAPTION_FONT_NOT_FOUND) return;
    yield fork(retryAutoSaveLater);
  }
}

// One auto-save pass: read context -> decide -> persist.
function* runAutoSave() {
  const context = yield call(getAutoSaveContext);
  if (!context) return; // auto-save off or no project yet

  const diff = computeAutoSaveDiff(context);
  if (!diff.isDirty) {
    yield putAutoSaveStatus(AUTO_SAVE_STATUS.SAVED);
    return;
  }
  if (isOverCharacterLimit(context.blocks)) {
    yield putAutoSaveStatus(AUTO_SAVE_STATUS.BLOCKED);
    return;
  }

  yield call(persistAutoSave, context, diff);
}

function* watchAutoSaveQueue() {
  const triggerChannel = yield actionChannel(actionTypes.AUTO_SAVE_TRIGGER);
  while (true) {
    yield take(triggerChannel);
    yield call(runAutoSave);
  }
}

function* triggerAutoSave() {
  yield put(actions.project.autoSaveTrigger());
}

function* markSavingOnEdit() {
  const project = yield select((s) => s.project);
  const { autoSaveEnabled, projectId } = project;
  if (!autoSaveEnabled || !projectId) return;
  // A deleted project can't be saved — keep the banner, don't flash "Saving".
  if (project.autoSaveStatus === AUTO_SAVE_STATUS.DELETED) return;
  // Over the character limit the project can't be persisted, so show BLOCKED
  // right away instead of flashing "Saving" before runAutoSave settles.
  if (isOverCharacterLimit(project.multiBlock?.blocks)) {
    yield putAutoSaveStatus(AUTO_SAVE_STATUS.BLOCKED);
    return;
  }
  yield putAutoSaveStatus(AUTO_SAVE_STATUS.SAVING);
}

function* revertSavedToIdle() {
  yield delay(AUTO_SAVE_SAVED_DURATION_MS);
  const status = yield select((s) => s.project.autoSaveStatus);
  if (status === AUTO_SAVE_STATUS.SAVED) {
    yield putAutoSaveStatus(AUTO_SAVE_STATUS.IDLE);
  }
}

function* watchRevertSavedToIdle() {
  yield takeLatest(
    (action) =>
      action.type === actionTypes.SET_AUTO_SAVE_STATUS &&
      action.payload === AUTO_SAVE_STATUS.SAVED,
    revertSavedToIdle,
  );
}

export default function* projectSaga() {
  yield all([
    takeEvery(isAutoSaveTriggerAction, markSavingOnEdit),
    debounce(AUTO_SAVE_DEBOUNCE_MS, isAutoSaveTriggerAction, triggerAutoSave),
    watchAutoSaveQueue(),
    watchRevertSavedToIdle(),
  ]);
}
